import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '@origo/origo-ui';
import { CaretDownIcon } from '@phosphor-icons/react';
import type { DispensasjonFormValues, DispensationItem } from '../../../types';
import DispensationForm, { type DispensationFieldErrors } from './DispensationForm';
import styles from './DispensationListItem.module.css';

interface DispensationListItemProps {
    item: DispensationItem;
    fieldErrors?: DispensationFieldErrors;
    /** Kalles når brukeren klikker Lagre med gjeldende draft. */
    onSave: (id: string, values: DispensasjonFormValues) => void;
    /** Åpner sidepanelet med tilknyttede dokumenter (F12.4). */
    onShowDocuments: (dispensasjonId: string) => void;
    /** Ber om sletting — containeren viser bekreftelse (#14731). */
    onDeleteRequest: (dispensasjonId: string) => void;
    isSaving?: boolean;
}

const FIELDS: ReadonlyArray<keyof DispensasjonFormValues> = [
    'dispensasjonType', 'dispensasjonFra', 'vedtaksdato', 'innvilgetDispensasjon',
    'varigDispensasjon', 'varighetFra', 'varighetTil', 'saksnummer', 'begrunnelse', 'beskrivelse',
];

const DispensationListItem: React.FC<DispensationListItemProps> = ({ item, fieldErrors, onSave, onShowDocuments, onDeleteRequest, isSaving }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<DispensasjonFormValues>(item.values);
    const contentId = useId();

    const isDirty = useMemo(
        () => FIELDS.some(f => draft[f] !== item.values[f]),
        [draft, item.values]
    );

    // Sync draft når backend-data endres (f.eks. etter lagring som refetcher),
    // men aldri over brukerens ulagrede endringer.
    useEffect(() => {
        if (!isDirty) setDraft(item.values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.values]);

    const handleChange = <K extends keyof DispensasjonFormValues>(field: K, value: DispensasjonFormValues[K]) => {
        setDraft(prev => ({ ...prev, [field]: value }));
    };

    const handleCancel = () => {
        setDraft(item.values);
        setOpen(false);
    };

    return (
        <li className={`${styles.item} ${open ? styles.open : ''}`}>
            <button
                type="button"
                className={styles.trigger}
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen(o => !o)}
            >
                <span className={styles.headingGroup}>
                    <span className={styles.itemTitle}>{item.tittel}</span>
                    <span className={styles.itemMeta}>{item.dato}</span>
                </span>
                <span className={styles.badges}>
                    {isDirty && <Chip variant="default">{t('dispensations.unsaved')}</Chip>}
                </span>
                <CaretDownIcon
                    size={20}
                    className={`${styles.indicator} ${open ? styles.indicatorOpen : ''}`}
                    aria-hidden
                />
            </button>
            <div id={contentId} role="region" aria-label={item.tittel} hidden={!open}>
                <DispensationForm
                    values={draft}
                    fieldErrors={fieldErrors}
                    onChange={handleChange}
                    onCancel={handleCancel}
                    onSave={() => onSave(item.id, draft)}
                    onShowDocuments={() => onShowDocuments(item.id)}
                    onDelete={() => onDeleteRequest(item.id)}
                    documentCount={item.documentCount}
                    saveDisabled={!isDirty}
                    isSaving={isSaving}
                />
            </div>
            {/* Skjult tekst for skjermlesere som beskriver toggle-handlingen */}
            <span className={styles.srOnly}>
                {open ? t('common.close') : t('common.edit')}
            </span>
        </li>
    );
};

export default DispensationListItem;
